import { transliterateCyrillicToLatin } from "@lib/search/cyrillic-transliteration";

const LETTER_OR_NUMBER = /[\p{L}\p{N}]/u;

export const normalizeSearchText = (value: string): string =>
  value.toLowerCase().replace(/ё/g, "е").replace(/\s+/g, " ").trim();

export const tokenizeSearchQuery = (value: string): string[] =>
  normalizeSearchText(value).split(" ").filter(Boolean);

const isLetterOrNumber = (char: string | undefined): boolean =>
  char != null && LETTER_OR_NUMBER.test(char);

/** True when `token` starts a word: beginning of text or after a non-letter/non-number. */
const tokenMatchesAtWordStart = (text: string, token: string): boolean => {
  if (!token || !text) {
    return false;
  }

  let from = 0;
  while (from <= text.length) {
    const index = text.indexOf(token, from);
    if (index === -1) {
      return false;
    }
    if (!isLetterOrNumber(text[index - 1])) {
      return true;
    }
    from = index + 1;
  }

  return false;
};

/**
 * Match a token only at the start of a word.
 * Direct prefix uses original text; Cyr→Lat prefix uses the transliterated haystack.
 */
const tokenMatchesHaystack = (originalHaystack: string, token: string): boolean => {
  if (tokenMatchesAtWordStart(originalHaystack, token)) {
    return true;
  }

  const latinToken = transliterateCyrillicToLatin(token);
  if (!latinToken) {
    return false;
  }

  const transliteratedHaystack = transliterateCyrillicToLatin(originalHaystack);
  if (transliteratedHaystack === originalHaystack && latinToken === token) {
    return false;
  }

  return tokenMatchesAtWordStart(transliteratedHaystack, latinToken);
};

/**
 * Order-independent AND match: every query token must start a word in haystack.
 * Cyrillic tokens also match the Cyr→Lat prefix of a word.
 */
export const matchesTokenizedSearch = (haystack: string, query: string): boolean => {
  const tokens = tokenizeSearchQuery(query);
  if (tokens.length === 0) {
    return true;
  }

  const originalHaystack = normalizeSearchText(haystack);
  if (!originalHaystack) {
    return false;
  }

  return tokens.every((token) => tokenMatchesHaystack(originalHaystack, token));
};
